import { ReactNode } from 'react';

import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';

export const announcementMap = {
  normal: 'normal',
  graident: 'gradient',
  error: 'error'
} as const;

export type AnnouncementType = keyof typeof announcementMap;

export interface Announcement {
  id: string;
  type?: AnnouncementType;
  message?: string;
  action?: (() => void) | null;
  actionLabel?: string;
  closable?: boolean;
  leadingIcon?: ReactNode;
  priority: number;
}

export interface InitialState {
  announcements: Record<string, Announcement>;
  showNotifications: boolean;
  showCoPilot: boolean;
  copilotChatId: string | null;
}

const initialState: InitialState = {
  announcements: {},
  showNotifications: false,
  showCoPilot: false,
  copilotChatId: null
};

type Reducers = {
  setAnnouncement: CaseReducer<InitialState, PayloadAction<Announcement>>;
  resetAnnouncement: CaseReducer<
    InitialState,
    PayloadAction<string | undefined>
  >;
  resetAllAnnouncements: CaseReducer<InitialState>;
  toggleNotifications: CaseReducer<InitialState>;
  toggleCoPilot: CaseReducer<InitialState>;
  closeNotifications: CaseReducer<InitialState>;
  setCopilotChatId: CaseReducer<InitialState, PayloadAction<string | null>>;
};

export const appSlice = createSlice<InitialState, Reducers, 'app'>({
  name: 'app',
  initialState,
  reducers: {
    setAnnouncement: (state, action) => {
      const announcement = action.payload;
      if (announcement.id) {
        state.announcements[announcement.id] = announcement;
      }
    },
    resetAnnouncement: (state, action) => {
      const id = action.payload;
      if (id && state.announcements[id]) {
        delete state.announcements[id];
      }
    },
    resetAllAnnouncements: state => {
      state.announcements = {};
    },
    toggleNotifications: state => {
      state.showNotifications = !state.showNotifications;
    },
    toggleCoPilot: state => {
      state.showCoPilot = !state.showCoPilot;
    },
    closeNotifications: state => {
      state.showNotifications = false;
    },
    setCopilotChatId: (state, action) => {
      state.copilotChatId = action.payload;
    }
  }
});

export const {
  setAnnouncement,
  resetAnnouncement,
  resetAllAnnouncements,
  toggleNotifications,
  toggleCoPilot,
  closeNotifications,
  setCopilotChatId
} = appSlice.actions;

export default appSlice.reducer;
